/**
 * Supabase Storage image variant helpers.
 *
 * We no longer use Storage "Image Transformations" (/storage/v1/render/image/…):
 * it is billed per distinct origin image transformed, so every displayed image
 * cost money. Instead, uploads (and a one-off backfill) write pre-sized WebP
 * siblings next to each public object:
 *
 *   <dir>/<stem>.<ext>          original (≤1600px; older uploads may be larger)
 *   <dir>/<stem>-w480.webp      width ≤ 480
 *   <dir>/<stem>-w960.webp      width ≤ 960
 *
 * These helpers only pick a URL — they never request a transformation. Every
 * variant URL is plain /storage/v1/object/public/… and served by the CDN.
 *
 * Variants can be missing (backfill not yet run, or an upload path that does
 * not generate them), so every <img> consumer of a variant URL must fall back
 * to the original on error (see OptimizedImage / optimizeBodyImages).
 */

const OBJECT_PUBLIC_SEGMENT = "/storage/v1/object/public/";

/** Widths for which a `-w<width>.webp` sibling is pre-generated, ascending. */
export const SUPABASE_IMAGE_VARIANT_WIDTHS = [480, 960] as const;

const VARIANT_SUFFIX_RE = /-w(?:480|960)\.webp$/i;
/** Formats the variant pipeline does not produce (vector / animation). */
const NO_VARIANT_EXT_RE = /\.(?:svg|gif)$/i;
const HAS_EXT_RE = /\.[a-z0-9]+$/i;

/**
 * True when the URL points at a Supabase Storage public object.
 */
export function isSupabasePublicObjectUrl(
  url: string | null | undefined,
): boolean {
  return !!url && url.includes(OBJECT_PUBLIC_SEGMENT);
}

/** Split `url` into [path, suffix] where suffix is the `?query#hash` tail. */
function splitTail(url: string): [string, string] {
  const q = url.indexOf("?");
  const h = url.indexOf("#");
  const cut = q === -1 ? h : h === -1 ? q : Math.min(q, h);
  return cut === -1 ? [url, ""] : [url.slice(0, cut), url.slice(cut)];
}

/** Variant width bucket for a requested width, or null → use the original. */
export function supabaseVariantWidthFor(width: number): 480 | 960 | null {
  for (const w of SUPABASE_IMAGE_VARIANT_WIDTHS) {
    if (width <= w) return w;
  }
  return null;
}

/**
 * True when `url` is a public-object URL for which a pre-sized variant can
 * exist (not already a variant, not SVG/GIF, has a file extension).
 */
export function hasSupabaseImageVariants(url: string): boolean {
  if (!url.includes(OBJECT_PUBLIC_SEGMENT)) return false;
  const [path] = splitTail(url);
  const file = path.slice(path.lastIndexOf("/") + 1);
  return (
    HAS_EXT_RE.test(file) &&
    !VARIANT_SUFFIX_RE.test(file) &&
    !NO_VARIANT_EXT_RE.test(file)
  );
}

/**
 * Picks the pre-generated variant for a Supabase public-object URL:
 * width ≤ 480 → `-w480.webp`, ≤ 960 → `-w960.webp`, otherwise the original
 * object URL. Any query string / hash is preserved. Already-variant URLs,
 * SVG/GIF, extension-less objects and non-storage URLs (unsplash, /public, …)
 * pass through unchanged.
 *
 * The result may 404 until variants exist — callers rendering it must fall
 * back to the original URL on error.
 *
 * @param url    Absolute image URL. Callers guard null/undefined.
 * @param width  Target width in px (~2x the rendered CSS width).
 */
export function supabaseImageVariant(url: string, width: number): string {
  if (!hasSupabaseImageVariants(url)) return url;
  const bucket = supabaseVariantWidthFor(width);
  if (bucket === null) return url;

  const [path, tail] = splitTail(url);
  const stemPath = path.replace(HAS_EXT_RE, "");
  return `${stemPath}-w${bucket}.webp${tail}`;
}
